import React from 'react';
import { motion } from 'framer-motion';
import { SlideLayout } from '../SlideLayout';
import { TrendingDown, TrendingUp, Clock, Zap, BarChart2 } from 'lucide-react';
export function ExecSummarySlide({
  isActive
}: {
  isActive?: boolean;
}) {
  const throughputRows = [{
    label: '$0.17/M',
    sublabel: 'Commodity',
    value: '52,000',
    unit: 'tok/sec needed',
    verdict: 'Impossible',
    verdictColor: 'text-chip-gpu',
    bg: 'bg-chip-gpu/10',
    border: 'border-chip-gpu/20'
  }, {
    label: '$3/M',
    sublabel: 'Interactive',
    value: '2,965',
    unit: 'tok/sec needed',
    verdict: 'Achievable',
    verdictColor: 'text-chip-fpga',
    bg: 'bg-chip-fpga/10',
    border: 'border-chip-fpga/20'
  }, {
    label: '$60/M',
    sublabel: 'Real-Time SLA',
    value: '150',
    unit: 'tok/sec needed',
    verdict: 'Trivial',
    verdictColor: 'text-brand-primary',
    bg: 'bg-brand-primary/10',
    border: 'border-brand-primary/20'
  }];
  return <SlideLayout slideNumber={2} totalSlides={21} title="Executive Summary" data-id="element-206">
      <div className="w-full h-full flex flex-col gap-4" data-id="element-207">
        {/* Header */}
        <motion.div initial={{
        opacity: 0,
        y: 16
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5
      }} className="text-center" data-id="element-208">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-brand-border bg-brand-surface text-brand-muted text-xs font-mono uppercase tracking-widest mb-3" data-id="element-209">
            Investor Brief
          </div>
          <h2 className="text-3xl md:text-5xl font-bold text-white leading-tight mb-2" data-id="element-210">
            The <span className="text-chip-fpga" data-id="element-211">FPGA Arbitrage</span>
          </h2>
          <p className="text-base md:text-lg text-brand-muted max-w-2xl mx-auto leading-relaxed" data-id="element-212">
            AI inference is fragmenting into two markets. One is a race to zero.
            The other pays a premium for deterministic latency GPUs can't
            deliver.
          </p>
        </motion.div>

        {/* Two Tier Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-id="element-213">
          <motion.div initial={{
          opacity: 0,
          x: -20
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          delay: 0.2,
          duration: 0.5
        }} className="bg-chip-gpu/10 border border-chip-gpu/30 rounded-xl p-4" data-id="element-214">
            <div className="flex items-center gap-2 mb-2" data-id="element-215">
              <TrendingDown size={14} className="text-chip-gpu" data-id="element-216" />
              <span className="text-chip-gpu text-xs font-mono uppercase tracking-widest" data-id="element-217">
                Commodity Tier – Race to Zero
              </span>
            </div>
            <div className="text-chip-gpu font-mono font-bold text-3xl md:text-4xl mb-2" data-id="element-218">
              $0.17<span className="text-lg text-brand-muted" data-id="element-219">/M tokens</span>
            </div>
            <p className="text-brand-muted text-sm leading-relaxed" data-id="element-220">
              Depreciated H100s flooding secondary markets at $1.49–2.10/hr.
              Needs{' '}
              <span className="text-white font-mono" data-id="element-221">~17,000 tok/sec</span> to
              break even. Competing with distressed hardware.
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          delay: 0.3,
          duration: 0.5
        }} className="bg-chip-fpga/10 border border-chip-fpga/40 rounded-xl p-4" data-id="element-222">
            <div className="flex items-center gap-2 mb-2" data-id="element-223">
              <TrendingUp size={14} className="text-chip-fpga" data-id="element-224" />
              <span className="text-chip-fpga text-xs font-mono uppercase tracking-widest" data-id="element-225">
                Premium Tier – Margin Machine
              </span>
            </div>
            <div className="text-chip-fpga font-mono font-bold text-3xl md:text-4xl mb-2" data-id="element-226">
              $3–60<span className="text-lg text-brand-muted" data-id="element-227">/M tokens</span>
            </div>
            <p className="text-brand-muted text-sm leading-relaxed" data-id="element-228">
              Voice agents, live coding, trading systems. Only{' '}
              <span className="text-white font-mono" data-id="element-229">~3,000 tok/sec</span> gives
              80% margin at $3/M. At $60/M:{' '}
              <span className="text-white font-mono" data-id="element-230">750 tok/sec</span>.
            </p>
          </motion.div>
        </div>

        {/* Throughput Box */}
        <motion.div initial={{
        opacity: 0,
        y: 16
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.4,
        duration: 0.5
      }} className="bg-brand-surface border border-brand-border rounded-xl p-4" data-id="element-231">
          <div className="flex items-center gap-2 mb-3" data-id="element-232">
            <BarChart2 size={14} className="text-brand-muted" data-id="element-233" />
            <span className="text-brand-muted text-xs font-mono uppercase tracking-widest" data-id="element-234">
              $175K 8-FPGA Box – Throughput for 80% Gross Margin
            </span>
          </div>
          <div className="grid grid-cols-3 gap-2" data-id="element-235">
            {throughputRows.map((row, i) => <motion.div key={row.label} initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.5 + i * 0.1
          }} className={`${row.bg} border ${row.border} rounded-lg p-3 text-center`} data-id="element-236">
                <div className="text-brand-muted text-xs font-mono mb-0.5" data-id="element-237">
                  {row.label} · {row.sublabel}
                </div>
                <div className={`font-mono font-bold text-xl md:text-2xl ${row.verdictColor}`} data-id="element-238">
                  {row.value}
                </div>
                <div className="text-brand-muted text-xs mb-1" data-id="element-239">{row.unit}</div>
                <div className={`font-bold text-sm ${row.verdictColor}`} data-id="element-240">
                  {row.verdict}
                </div>
              </motion.div>)}
          </div>
        </motion.div>

        {/* Bottom two columns */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-id="element-241">
          <motion.div initial={{
          opacity: 0,
          y: 12
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.7
        }} className="bg-brand-surface border border-brand-border rounded-xl p-4" data-id="element-242">
            <div className="flex items-center gap-2 mb-2" data-id="element-243">
              <Zap size={14} className="text-chip-fpga" data-id="element-244" />
              <span className="text-brand-muted text-xs font-mono uppercase tracking-widest" data-id="element-245">
                Why FPGAs Win the Premium Tier
              </span>
            </div>
            <ul className="space-y-1.5" data-id="element-246">
              <li className="text-sm text-brand-muted leading-snug" data-id="element-247">
                <span className="text-white font-semibold" data-id="element-248">
                  Deterministic sub-10ms latency:
                </span>{' '}
                Enables $15–60/M SLA pricing commodity providers can't guarantee
              </li>
              <li className="text-sm text-brand-muted leading-snug" data-id="element-249">
                <span className="text-white font-semibold" data-id="element-250">
                  Memory-bandwidth optimized:
                </span>{' '}
                1.9× bandwidth per box vs single H100; efficient at low batch
                sizes where GPUs idle
              </li>
            </ul>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 12
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.8
        }} className="bg-brand-surface border border-brand-border rounded-xl p-4" data-id="element-251">
            <div className="flex items-center gap-2 mb-2" data-id="element-252">
              <Clock size={14} className="text-brand-primary" data-id="element-253" />
              <span className="text-brand-muted text-xs font-mono uppercase tracking-widest" data-id="element-254">
                Market Timing
              </span>
            </div>
            <ul className="space-y-1.5" data-id="element-255">
              <li className="text-sm text-brand-muted leading-snug flex gap-2" data-id="element-256">
                <span className="text-brand-primary mt-0.5 shrink-0" data-id="element-257">•</span>
                Inference spend overtaking training in 2026; real-time use cases
                growing 3× faster than batch
              </li>
              <li className="text-sm text-brand-muted leading-snug flex gap-2" data-id="element-258">
                <span className="text-chip-gpu mt-0.5 shrink-0" data-id="element-259">•</span>
                H100 spot collapse (65% price drop since 2024) makes commodity
                inference a race to zero
              </li>
              <li className="text-sm text-brand-muted leading-snug flex gap-2" data-id="element-260">
                <span className="text-chip-fpga mt-0.5 shrink-0" data-id="element-261">•</span>
                Enterprises pay 10–60× premiums for latency guarantees today —
                that spread is your wedge
              </li>
            </ul>
          </motion.div>
        </div>
      </div>
    </SlideLayout>;
}